import { issueSeverity, repairLine, worded } from "./rule-catalog";
import { ruleImpl } from "./rule-impl";
import {
  exposureZoneAt,
  getVisibilityRuleLeaves,
  isValidExposureBoundary,
  resolveExposureMarkIndexes,
} from './index';
import type {
  QuestionnaireExposureBoundary,
  QuestionnaireExposureMarkIndexes,
  QuestionnaireExposureZone,
  QuestionnaireValidationIssue,
} from './index';
import { profilingRoleOf } from './profiling';
import { questionAssetSurfaces } from './question-asset-surfaces';
import { withStudyBinding } from './study-binding';
import { studySpecOf } from './study-spec';
import { opensLaneSlot } from '../study-design';

type RecordLike = Record<string, any>;
const record = (value: unknown): RecordLike =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const list = (value: unknown): Array<RecordLike> =>
  Array.isArray(value) ? value.filter(v => v && typeof v === 'object') : [];

/**
 * The tested assets a study shows twins: the catalog entries its StudySpec
 * resolves as `stimulus` materials. A catalog such as a list of pains is
 * never shown, so it is never exposure, and a draft without a spec has no
 * stimulus. Every exposure check reads this one set.
 */
export function stimulusAssetsOf(draft: unknown): { assets: Array<RecordLike> } {
  const source = record(draft);
  const stimulus = new Set(
    (studySpecOf(source)?.resolved.materials ?? [])
      .filter(material => material.role === 'stimulus')
      .map(material => material.assetId),
  );
  return { assets: list(record(source.testedAssets).assets).filter(a => stimulus.has(a.id)) };
}

/**
 * A question surface that directly renders one of `testedAssets` before the
 * runner can apply the declared reveal: a binding, or rows that are the tested
 * assets. Asset refs identify answer/report subjects and render nothing, so a
 * typed asset-choice roster (`assetBinding.mode="none"` with option refs) is
 * not a surface here; listed before the reveal it is refused on its own
 * account (`pre_exposure_asset_listed`, decision 24), since it would list what
 * is tested before anyone sees it.
 */
export function preRevealStimulusSurface(
  question: unknown,
  testedAssets: unknown,
): 'assetBinding' | 'response.rows' | null {
  const surfaces = questionAssetSurfaces(question, testedAssets);
  if (surfaces.some(surface => surface.kind === 'binding' && surface.assetIds.length > 0)) {
    return 'assetBinding';
  }
  return surfaces.some(surface => surface.kind === 'tested_rows' && surface.assetIds.length > 0)
    ? 'response.rows'
    : null;
}

/** A question-level ref with one of these roles names versions shown together. */
const JOINT_REF_ROLES = new Set<string | null>(['matrix_row', 'choice', 'comparison']);

/**
 * Exact asset identities a question carries, read from `questionAssetSurfaces`:
 * every asset it references (`referenced`); those it shows together as a list
 * (`joint`: answers, categories, rows, measures, tested rows, and question refs
 * in a joint role); and those its binding shows (`bound`).
 */
export function questionExposureAssetIds(question: unknown, testedAssets: unknown): {
  referenced: Array<string>; joint: Array<string>; bound: Array<string>;
} {
  const referenced = new Set<string>();
  const joint = new Set<string>();
  const bound: Array<string> = [];
  questionAssetSurfaces(question, testedAssets).forEach(surface => {
    if (surface.kind === 'binding') {
      bound.push(...surface.assetIds);
      return;
    }
    const ids = surface.kind === 'tested_rows' ? surface.assetIds : [surface.assetId];
    ids.forEach(id => {
      referenced.add(id);
      if (surface.kind !== 'ref' || JOINT_REF_ROLES.has(surface.role)) joint.add(id);
    });
  });
  return { referenced: [...referenced], joint: [...joint], bound };
}

/**
 * A question fixed on some of the tested versions, not all: unbound, it names
 * fewer versions than the study shows (`lane_question_fixed_asset_subset`).
 * The engine has no lane for it, whether the lanes repeat or run once: the
 * draft rule below refuses it there and the placer puts it in the shared view
 * (`zoneRangeOf`), one predicate for both.
 */
export function fixedOnSomeVersions(question: unknown, stimulus: unknown): boolean {
  const referenced = questionExposureAssetIds(question, stimulus).referenced.length;
  const binding = (question as { assetBinding?: { mode?: unknown } } | null)?.assetBinding;
  return binding?.mode === 'none' && referenced > 0 && referenced < list(record(stimulus).assets).length;
}

/**
 * Where a question lists a stimulus asset as one of its answers, categories,
 * rows or joint-role refs (`questionExposureAssetIds(...).joint`): the first
 * such surface's path, or null. The one predicate of `pre_exposure_asset_listed`:
 * the gate below and the applier's move before the reveal both read it over
 * the stimulus assets.
 */
export function jointListPath(question: unknown, stimulus: unknown): string | null {
  const surface = questionAssetSurfaces(question, stimulus).find(
    candidate =>
      candidate.kind === 'item' ||
      (candidate.kind === 'tested_rows' && candidate.assetIds.length > 0) ||
      (candidate.kind === 'ref' && JOINT_REF_ROLES.has(candidate.role)),
  );
  return surface ? surface.path : null;
}
ruleImpl('pre_exposure_asset_listed', jointListPath);

/**
 * The tested versions a question shows at once. An isolated design (or the
 * question's own per-asset binding) narrows catalog-wide candidates to one
 * version per lane, so only its joint surfaces (rows, choices, comparison
 * refs) count; otherwise its bound and referenced assets are shown together
 * too. The lane rule below and the derived shared-view mark
 * (`exposureCeilingIndex`, Nest) read this one fact.
 */
export function simultaneousExposureAssetIds(
  question: unknown,
  testedAssets: unknown,
  monadic: boolean,
): Array<string> {
  const scope = questionExposureAssetIds(question, testedAssets);
  const isolated = monadic || record(record(question).assetBinding).mode === 'per_asset';
  return [...new Set(isolated ? scope.joint : [...scope.joint, ...scope.referenced, ...scope.bound])];
}

/**
 * Whether the lane phase repeats per asset: some lane-zone question opens a
 * slot (`opensLaneSlot`) under the design. Without one the lane runs once as
 * shared context, so a question fixed on one asset is asked once about it.
 * Not exported: whether the survey runs separate passes is
 * `exposureZonesOf(...).separatePasses`, the one gate. The monadic advice
 * below reads it over the bindings normalize would stamp.
 */
function lanesRepeat(
  questions: ReadonlyArray<{ assetBinding?: unknown }>,
  indexes: QuestionnaireExposureMarkIndexes,
  monadic: boolean,
): boolean {
  return questions.some((q, index) =>
    exposureZoneAt(index, indexes) === 'lane' && opensLaneSlot(q.assetBinding, monadic));
}

/** What `exposureZonesOf` reads off a draft. */
export interface QuestionnaireExposureZones {
  zones: ReadonlyMap<string, QuestionnaireExposureZone>;
  separatePasses: boolean;
}

/**
 * A draft's exposure zones: each question's zone between the declared marks,
 * and whether the survey runs those zones as separate passes.
 *
 * - `zones`: by stable key, in draft order, `exposureZoneAt` against the
 *   declared marks. This is where a question sits beside the tested items,
 *   the line the builder draws.
 * - `separatePasses`: the lane repeats once for each version, so the part
 *   before exposure, each version and the shared view are each their own
 *   pass (engine `monadic.partition_monadic`). False when the marks cross
 *   (`exposure_marks_cross` refuses it) or no lane question opens a slot
 *   (`opensLaneSlot`): the survey is then one pass, and a condition or a skip
 *   may cross zones.
 *
 * Null when the draft has no exposure to order: no valid declared boundary,
 * or a study that shows twins no stimulus (`stimulusAssetsOf`).
 *
 * `design` is the compile's; otherwise the draft's StudySpec design. Every
 * reader of "does this survey run in passes" reads this one gate: the
 * dependency and route rules below, answerability, the runner's delivery
 * (`twinExposureOf`), the compiler's exposure scopes and routing triggers
 * and the builder's logic pickers.
 */
export function exposureZonesOf(
  draft: unknown,
  design?: string,
): QuestionnaireExposureZones | null {
  const source = record(draft);
  if (!isValidExposureBoundary(source.exposureBoundary)) return null;
  if (!stimulusAssetsOf(source).assets.length) return null;
  const questions = list(source.researchQuestions);
  const indexes = resolveExposureMarkIndexes(questions, source.exposureBoundary);
  const monadic = (design ?? studySpecOf(source)?.resolved.design) === 'twin_monadic';
  return {
    zones: new Map(
      questions.map((question, index) => [String(question.stableKey), exposureZoneAt(index, indexes)]),
    ),
    separatePasses:
      indexes.revealIndex < indexes.sharedViewIndex && lanesRepeat(questions, indexes, monadic),
  };
}

/** The questions a question's condition, route or carried list reads: the answers it needs first. */
export function dependencyKeysOf(question: RecordLike): Array<string> {
  return [
    ...getVisibilityRuleLeaves(question.visibilityRule),
    ...getVisibilityRuleLeaves(record(question.routingRule).when),
  ].map(leaf => record(leaf).dependsOnStableKey)
    .concat(record(record(question.response).choicesFrom).sourceStableKey)
    .filter((key): key is string => typeof key === 'string');
}

/**
 * A question that asks about the respondent and shows no tested version: it
 * carries a profile role (`profilingRoleOf`, stamped by PLAN and by the revision
 * brief) and the binding assembly minted it, `none` (Python
 * `build_asset_binding` is the one owner of "a profiling question shows
 * nothing"; this reads it). Asked inside a repeating lane it is asked once for
 * each version, so its answer splits into one per version (AD-15: age three
 * times). A before/after pair is minted a binding and is not one.
 */
export function isRespondentProfile(question: unknown, testedAssets: unknown): boolean {
  const source = record(question);
  if (profilingRoleOf(source.tags) === null || record(source.assetBinding).mode !== 'none') return false;
  const shown = questionExposureAssetIds(source, testedAssets);
  return shown.referenced.length === 0 && shown.bound.length === 0;
}

/** Whether the survey asks its lane once for each version, over the bindings normalize leaves (`withStudyBinding`). */
export function lanesRepeatOver(
  questions: Array<RecordLike>,
  boundary: QuestionnaireExposureBoundary,
  source: RecordLike,
  design?: string,
): boolean {
  const indexes = resolveExposureMarkIndexes(questions, boundary);
  const monadic = (design ?? studySpecOf(source)?.resolved.design) === 'twin_monadic';
  const spec = studySpecOf(source);
  return lanesRepeat(
    questions.map((q, index) => ({
      assetBinding: withStudyBinding(q, exposureZoneAt(index, indexes), spec).assetBinding,
    })),
    indexes,
    monadic,
  );
}

/**
 * Whether one question's answer is available where another question runs.
 *
 * The repeated lane is executed once for each version.  Its answers therefore
 * have no single value in the subsequent shared pass.  Keep that one
 * execution fact here so authoring affordances and the write validator make
 * the same decision.  An undeclared/invalid boundary, or a draft without
 * stimulus, has no declared exposure scope and leaves ordinary dependencies
 * available for repair.
 */
export function isQuestionDependencyAvailableAtExposure(
  draft: unknown,
  targetStableKey: string,
  sourceStableKey: string,
  design?: string,
): boolean {
  const exposure = exposureZonesOf(draft, design);
  return !(
    exposure?.separatePasses &&
    exposure.zones.get(targetStableKey) === 'shared_view' &&
    exposure.zones.get(sourceStableKey) === 'lane'
  );
}

/**
 * Where a lane exclusion (`QuestionLaneExclusion`, D-b) may sit: on a question
 * the survey asks once per version in its own pass (`exposureZonesOf`), and
 * naming, once, a version the study shows (`stimulusAssetsOf`). Anywhere else
 * no lane copy exists for the engine to skip, and the exclusion would hold for
 * nobody. Its condition is judged with every other (`conditionExprIssues`).
 */
function laneExclusionIssues(source: RecordLike, design?: string): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  const questions = list(source.researchQuestions);
  if (!questions.some(q => Array.isArray(q.laneExclusions))) return issues;
  const exposure = exposureZonesOf(source, design);
  const versions = new Set(stimulusAssetsOf(source).assets.map(asset => asset.stableKey));
  questions.forEach((q, index) => {
    if (!Array.isArray(q.laneExclusions)) return;
    const path = `researchQuestions[${index}]`;
    if (!exposure?.separatePasses || exposure.zones.get(String(q.stableKey)) !== 'lane') {
      issues.push({ path: `${path}.laneExclusions`, code: 'lane_exclusion_outside_lane', severity: issueSeverity('lane_exclusion_outside_lane'), message: repairLine('lane_exclusion_outside_lane') });
      return;
    }
    const named = new Set<unknown>();
    q.laneExclusions.forEach((exclusion: unknown, exclusionIndex: number) => {
      const asset = record(exclusion).assetStableKey;
      if (!versions.has(asset) || named.has(asset)) {
        issues.push({
          path: `${path}.laneExclusions[${exclusionIndex}].assetStableKey`,
          code: 'lane_exclusion_asset_unknown',
          severity: issueSeverity('lane_exclusion_asset_unknown'),
          message: repairLine('lane_exclusion_asset_unknown', { assetKey: String(asset) }),
        });
      }
      named.add(asset);
    });
  });
  return issues;
}

/**
 * Validate declared time against explicit scope BEFORE a stamp can erase a
 * contradiction. Missing boundaries are legacy/undeclared input, not invented
 * marks. All new compiled instruments carry the existing versioned boundary.
 *
 * Scope is read over the study's stimulus (`stimulusAssetsOf`). A study that
 * shows twins nothing has no exposure to order: its stored marks are neither
 * checked nor shown, so they never refuse an edit.
 *
 * `design` is the compile's; otherwise the draft's StudySpec design (plan
 * Stage 3, S3.4 row 22). A draft without a spec reads as a study with no
 * recorded choice, comparative, as the compile does.
 */
/**
 * The marks cross: the shared view starts at or before the reveal, so a
 * question would be both before exposure and in the shared view. The one
 * decider of `exposure_marks_cross`: the validator reads it on every draft,
 * and the applier on the boundary a `set_exposure_boundary` leaves, at that
 * operation (wave H T3).
 */
export function exposureMarksCrossIssue(
  questions: ReadonlyArray<{ stableKey?: string | null }>,
  boundary: Parameters<typeof resolveExposureMarkIndexes>[1],
): QuestionnaireValidationIssue | null {
  const indexes = resolveExposureMarkIndexes(questions, boundary);
  return indexes.revealIndex >= indexes.sharedViewIndex
    ? {
        path: 'exposureBoundary',
        code: 'exposure_marks_cross',
        severity: issueSeverity('exposure_marks_cross'),
        message: worded('exposure_marks_cross', 'Shared view cannot start before the reveal. No question is both pre-exposure and shared view.'),
      }
    : null;
}
ruleImpl('exposure_marks_cross', exposureMarksCrossIssue);

export function exposureCoherenceIssues(draft: unknown, design?: string): Array<QuestionnaireValidationIssue> {
  const source = record(draft);
  const boundary = source.exposureBoundary;
  const issues: Array<QuestionnaireValidationIssue> = laneExclusionIssues(source, design);
  if (boundary === undefined || boundary === null) return issues;
  const fail = (
    path: string,
    message: string,
    code:
      | 'exposure_boundary_invalid'
      | 'exposure_mark_question_missing'
      | 'pre_exposure_question_binds_stimulus'
      | 'pre_exposure_asset_listed'
      | 'lane_question_shows_several_versions'
      | 'profile_question_in_lane'
      | 'shared_view_question_per_version'
      | 'lane_question_fixed_asset_subset'
      | 'shared_view_depends_on_lane_answer'
      | 'route_target_other_pass',
  ) => issues.push({ path, code, severity: issueSeverity(code), message });
  if (!isValidExposureBoundary(boundary)) {
    fail('exposureBoundary', repairLine('exposure_boundary_invalid'), 'exposure_boundary_invalid');
    return issues;
  }
  const stimulus = stimulusAssetsOf(source);
  const catalog = stimulus.assets;
  if (!catalog.length) return issues;
  const questions = list(source.researchQuestions);
  for (const mark of ['reveal', 'sharedView'] as const) {
    const key = boundary[mark]?.stableKey;
    if (key && !questions.some(q => q.stableKey === key)) {
      fail(`exposureBoundary.${mark}`, repairLine('exposure_mark_question_missing', { mark: String(mark), key: String(key) }), 'exposure_mark_question_missing');
    }
  }
  const indexes = resolveExposureMarkIndexes(questions, boundary);
  const crossing = exposureMarksCrossIssue(questions, boundary);
  if (crossing) issues.push(crossing);
  if (issues.length) return issues;
  const monadic = (design ?? studySpecOf(source)?.resolved.design) === 'twin_monadic';
  const passes = exposureZonesOf(source, design)?.separatePasses === true;
  // A monadic design shows each version on its own only in lanes that
  // repeat. With no lane question that opens one, the engine runs a single
  // pass with every version shown together: a comparison under the monadic
  // design. A lane question the author left unbound runs with the binding
  // normalize stamps on it (`studyBindingFor`), so the rule reads the same
  // before and after the stamp, and so does the profile-question rule below.
  // Raised at the user's severity, the advice the builder shows; a model's
  // batch is refused (`refusesWrite`).
  const runsEachVersion = lanesRepeatOver(questions, boundary, source, design);
  if (monadic && !runsEachVersion) {
    issues.push({
      path: 'exposureBoundary',
      code: 'monadic_design_without_lane',
      severity: issueSeverity('monadic_design_without_lane'),
      message: repairLine('monadic_design_without_lane'),
    });
  }
  // When the lanes repeat, the engine asks the shared view in its own pass,
  // seeded with the answers given before the reveal and never a lane's
  // (`monadic.build_sub_survey`, `prior_answers`): each version has its own
  // answer, so none is the answer there. A condition, route or piped list on
  // a lane question never holds, and the question would never be asked.
  questions.forEach((q, index) => {
    const zone = exposureZoneAt(index, indexes);
    const scope = questionExposureAssetIds(q, stimulus);
    const path = `researchQuestions[${index}]`;
    // Constraint 2: a direct question-level binding or tested-asset grid
    // cannot run before the reveal. Otherwise, a question whose answers,
    // categories, rows or joint-role refs are stimulus assets cannot either
    // (decision 24): it would list what is tested before anyone sees it, and
    // its answers read as the assets do (a message's copy, a product's
    // price). A brief that lists them before the reveal uses plain named
    // options.
    if (zone === 'pre') {
      const preRevealSurface = preRevealStimulusSurface(q, stimulus);
      const listed = preRevealSurface === null ? jointListPath(q, stimulus) : null;
      if (preRevealSurface !== null) {
        fail(`${path}.${preRevealSurface}`, repairLine('pre_exposure_question_binds_stimulus'), 'pre_exposure_question_binds_stimulus');
      } else if (listed !== null) {
        fail(`${path}.${listed}`, repairLine('pre_exposure_asset_listed'), 'pre_exposure_asset_listed');
      }
    }
    // Its own code, so the refusal says why (QA3 F11 review P5): a question
    // that shows each twin its own version cannot run once every version is
    // on screen. Its binding is never released into the shared view: which
    // version it asks about would be ambiguous there.
    if (zone === 'shared_view' && q.assetBinding?.mode === 'per_asset' && scope.bound.length > 1) {
      fail(path, repairLine('shared_view_question_per_version'), 'shared_view_question_per_version');
    }
    if (dependencyKeysOf(q).some(key =>
      !isQuestionDependencyAvailableAtExposure(source, q.stableKey, key, design),
    )) {
      fail(path, repairLine('shared_view_depends_on_lane_answer'), 'shared_view_depends_on_lane_answer');
    }
    // The same passes run each zone on its own (before the reveal, once per
    // version, the shared view), so a skip lands only inside its own zone.
    const route = record(q.routingRule);
    if (passes && route.action === 'skip_to') {
      const targetIndex = questions.findIndex(t => t.stableKey === route.targetStableKey);
      if (targetIndex >= 0 && exposureZoneAt(targetIndex, indexes) !== zone) {
        fail(path, repairLine('route_target_other_pass'), 'route_target_other_pass');
      }
    }
    if (zone !== 'lane') return;
    // A question about the respondent is asked once for each version in a
    // repeating lane. PLAN's marks (`derive_exposure_marks`) keep it out; what
    // is left sits where a writer, or the marketer's own line, put it.
    if (runsEachVersion && isRespondentProfile(q, stimulus)) {
      fail(path, repairLine('profile_question_in_lane'), 'profile_question_in_lane');
    }
    // The engine's rule (`monadic._declared_exposure_phases`), which admits
    // the run: an unbound question fixed on some of the versions has no place
    // in the lane phase, whether the lanes repeat (it would be asked in every
    // lane) or run once. Its own code, so the refusal says why, and so the
    // write gate can let the write that adds an asset commit before the
    // questions take it in, when every version the question leaves out is
    // one that write adds (the backend's `causedByAddedAssets`;
    // `tests/fixtures/study-spec/twin-exposure-cases.json` holds both
    // languages to the rule).
    if (fixedOnSomeVersions(q, stimulus)) {
      fail(path, repairLine('lane_question_fixed_asset_subset'), 'lane_question_fixed_asset_subset');
    }
    // Catalog-wide stimulus refs are candidates narrowed by an isolated lane;
    // actual asset rows/choices/comparison refs are visible together and are not.
    if (simultaneousExposureAssetIds(q, stimulus, monadic).length > 1) {
      fail(path, repairLine('lane_question_shows_several_versions'), 'lane_question_shows_several_versions');
    }
  });
  return issues;
}

// Each rule below is decided by the function named (wave H T3, move 11).
ruleImpl('exposure_boundary_invalid', exposureCoherenceIssues);
ruleImpl('exposure_mark_question_missing', exposureCoherenceIssues);
ruleImpl('lane_question_fixed_asset_subset', exposureCoherenceIssues);
ruleImpl('lane_question_shows_several_versions', exposureCoherenceIssues);
ruleImpl('profile_question_in_lane', exposureCoherenceIssues);
ruleImpl('monadic_design_without_lane', exposureCoherenceIssues);
ruleImpl('route_target_other_pass', exposureCoherenceIssues);
ruleImpl('shared_view_depends_on_lane_answer', exposureCoherenceIssues);
ruleImpl('lane_exclusion_asset_unknown', laneExclusionIssues);
ruleImpl('lane_exclusion_outside_lane', laneExclusionIssues);
